import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, forwardRef } from 'react';
import { Video, ExternalLink } from 'lucide-react';
import { ContentModal } from './ContentModal';
import { Button } from '../button/Button';
export const VideoUploadModal = forwardRef(({ isOpen, onClose, onVideoSelect, zIndex = 99999 }, _ref) => {
    const [urlInput, setUrlInput] = useState('');
    const [error, setError] = useState('');
    const [embedUrl, setEmbedUrl] = useState('');
    const getEmbedUrl = (inputUrl) => {
        // Convert YouTube URLs
        const youtubeMatch = inputUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
        if (youtubeMatch) {
            return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
        }
        // Convert Vimeo URLs
        const vimeoMatch = inputUrl.match(/vimeo\.com\/(\d+)/);
        if (vimeoMatch) {
            return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
        }
        // If it's already an embed URL or other format, use as is
        return inputUrl;
    };
    const handleUrlChange = (url) => {
        setUrlInput(url);
        setError('');
        if (url.trim()) {
            const embed = getEmbedUrl(url.trim());
            setEmbedUrl(embed);
        }
        else {
            setEmbedUrl('');
        }
    };
    const handleUrlSubmit = () => {
        if (urlInput.trim()) {
            const finalUrl = getEmbedUrl(urlInput.trim());
            onVideoSelect(finalUrl);
            onClose();
        }
    };
    const isValidVideoUrl = (url) => {
        const patterns = [
            /youtube\.com\/watch\?v=/,
            /youtu\.be\//,
            /vimeo\.com\/\d+/,
            /\.mp4$/,
            /\.webm$/,
            /\.ogg$/
        ];
        return patterns.some(pattern => pattern.test(url));
    };
    // Force close any open tippy popovers when modal opens
    useEffect(() => {
        if (isOpen) {
            // Hide all tippy instances
            const tippyInstances = document.querySelectorAll('[data-tippy-root]');
            tippyInstances.forEach(instance => {
                const element = instance;
                if (element._tippy) {
                    element._tippy.hide();
                }
            });
        }
    }, [isOpen]);
    return (_jsxs(ContentModal, { isOpen: isOpen, onClose: onClose, title: "Add Video", size: "lg", zIndex: zIndex, footer: _jsxs("div", { className: "flex justify-end gap-2", children: [_jsx(Button, { onClick: onClose, variant: "outline", children: "Cancel" }), _jsx(Button, { onClick: handleUrlSubmit, disabled: !urlInput.trim() || !isValidVideoUrl(urlInput), children: "Add Video" })] }), children: [error && (_jsx("div", { className: "mb-4 p-3 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm", children: error })), _jsxs("div", { className: "mb-4", children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2", children: "Video URL" }), _jsx("div", { className: "flex gap-2", children: _jsx("input", { type: "url", value: urlInput, onChange: (e) => handleUrlChange(e.target.value), placeholder: "https://youtube.com/watch?v=... or https://vimeo.com/...", className: "field-text flex-1 px-3 py-2 border border-field rounded-md bg-background dark:bg-white/5 text-gray-900 dark:text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent", onKeyDown: (e) => {
                                if (e.key === 'Enter') {
                                    handleUrlSubmit();
                                }
                            } }) }), _jsx("p", { className: "text-xs text-gray-500 dark:text-gray-400 mt-1", children: "Supports YouTube, Vimeo, and direct video file URLs" })] }), embedUrl && (_jsxs("div", { className: "mb-4", children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2", children: "Preview" }), _jsx("div", { className: "aspect-video bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden border", children: _jsx("iframe", { src: embedUrl, title: "Video preview", className: "w-full h-full", frameBorder: "0", allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture", allowFullScreen: true }) })] })), _jsx("div", { className: "bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4", children: _jsxs("div", { className: "flex items-start gap-3", children: [_jsx(Video, { className: "h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" }), _jsxs("div", { children: [_jsx("h4", { className: "text-sm font-medium text-blue-900 dark:text-blue-100 mb-1", children: "Supported Video Sources" }), _jsxs("ul", { className: "text-sm text-blue-700 dark:text-blue-300 space-y-1", children: [_jsxs("li", { className: "flex items-center gap-1", children: [_jsx(ExternalLink, { className: "h-3 w-3" }), "YouTube videos (youtube.com, youtu.be)"] }), _jsxs("li", { className: "flex items-center gap-1", children: [_jsx(ExternalLink, { className: "h-3 w-3" }), "Vimeo videos (vimeo.com)"] }), _jsxs("li", { className: "flex items-center gap-1", children: [_jsx(ExternalLink, { className: "h-3 w-3" }), "Direct video files (.mp4, .webm, .ogg)"] })] })] })] }) })] }));
});
VideoUploadModal.displayName = 'VideoUploadModal';
//# sourceMappingURL=VideoUploadModal.js.map